import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback, useEffect } from "react";
import {
  FaSeedling,
  FaChartLine,
  FaArrowTrendDown,
  FaSkull,
} from "react-icons/fa6";
import { LuBaby } from "react-icons/lu";
import { GiFruitTree } from "react-icons/gi";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import { PiChartLineDownBold } from "react-icons/pi";
import Curve from "./Curve";
import Stage from "./Stage";
import { IconType } from "react-icons";
import { AssessmentScoringResult } from "types/assessmentTypes";
import { useIndustryLifeCycle } from "../helper/reportHelper";
import { Company } from "types/companyTypes";
import BottomCards from "./BottomCards";

export type StageType = {
  id: number;
  title: string;
  color: string;
  x: number;
  y: number;
  Icon: IconType;
};

const stages: StageType[] = [
  { id: 1, title: "INFANCY", color: "#2094F3", x: 7, y: 80, Icon: LuBaby },
  { id: 2, title: "GROWTH", color: "#11B8C7", x: 16, y: 68, Icon: FaSeedling },
  {
    id: 3,
    title: "MATURITY",
    color: "#2CBF62",
    x: 29,
    y: 49,
    Icon: FaChartLine,
  },
  {
    id: 4,
    title: "EVOLUTION",
    color: "#74C332",
    x: 40,
    y: 31,
    Icon: GiFruitTree,
  },
  {
    id: 5,
    title: "STABILITY",
    color: "#F3BE17",
    x: 54,
    y: 15,
    Icon: IoShieldCheckmarkOutline,
  },
  {
    id: 6,
    title: "EARLY_DECLINE",
    color: "#F38C18",
    x: 70,
    y: 31,
    Icon: PiChartLineDownBold,
  },
  {
    id: 7,
    title: "DECLINE",
    color: "#EF4B4B",
    x: 82,
    y: 52,
    Icon: FaArrowTrendDown,
  },
  { id: 8, title: "DEATH", color: "#8A35E4", x: 91, y: 77, Icon: FaSkull },
];

const stageKeyToIndex: Record<string, number> = {
  INFANCY: 1,
  GROWTH: 2,
  MATURITY: 3,
  EVOLUTION: 4,
  STABILITY: 5,
  EARLY_DECLINE: 6,
  DECLINE: 7,
  DEATH: 8,
};

interface IndustryLifeCycleProps {
  scoringResults?: AssessmentScoringResult[];
  company: Company;
  score?: number;
  position?: string;
  showBottomCards?: boolean;
  disableAnimation?: boolean;
  pdfMode?: boolean;
}

export default function IndustryLifeCycle({
  scoringResults,
  company,
  score,
  position,
  showBottomCards = true,
  disableAnimation = false,
  pdfMode = false,
}: IndustryLifeCycleProps) {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const useDirectProps = score !== undefined && position !== undefined;

  const industrySectorKey = company?.industrySector?.key;
  const lifeCycleResult = useIndustryLifeCycle(
    useDirectProps ? [] : scoringResults || [],
    useDirectProps ? undefined : industrySectorKey
  );

  const companyData = useMemo(() => {
    if (useDirectProps) {
      const stageIndex = stageKeyToIndex[position] || 1;
      return {
        name: company?.name || "-",
        score: Math.round(score),
        stage: stageIndex,
        stageName: position || "INFANCY",
      };
    } else if (lifeCycleResult) {
      return {
        name: company?.name || "-",
        score: Math.round(lifeCycleResult.totalScore * 100),
        stage:
          stages.findIndex((s) => s.title === lifeCycleResult.stageKey) + 1 ||
          1,
        stageName: lifeCycleResult.stageKey,
      };
    }
    return {
      name: company.name,
      score: 0,
      stage: 1,
      stageName: "-",
    };
  }, [company.name, lifeCycleResult, useDirectProps, score, position]);

  const [activeStageIndex, setActiveStageIndex] = useState(
    disableAnimation ? 7 : 0
  );
  const [animationComplete, setAnimationComplete] = useState(disableAnimation);

  const translatedStages = useMemo(
    () =>
      stages.map((stage) => ({
        ...stage,
        title: t(`assessment.industryLifeCycle.stages.${stage.title}`),
      })),
    [t]
  );

  const currentStageIndex = useMemo(
    () => translatedStages.findIndex((stage) => stage.id === companyData.stage),
    [companyData.stage, translatedStages]
  );

  const currentStage = useMemo(
    () => translatedStages[currentStageIndex],
    [translatedStages, currentStageIndex]
  );

  const handleStageActivation = useCallback(
    (progress: number) => {
      if (disableAnimation) {
        setActiveStageIndex(7);
        setAnimationComplete(true);
        return;
      }

      const totalStages = translatedStages.length;
      const newIndex = Math.min(
        Math.floor(progress * totalStages),
        totalStages - 1
      );

      setActiveStageIndex((prev) => (prev !== newIndex ? newIndex : prev));

      if (progress >= 1 && !animationComplete) {
        setAnimationComplete(true);
      }
    },
    [disableAnimation, translatedStages.length, animationComplete]
  );

  const CHART_WIDTH = 650;

  // PDF MODE
  if (pdfMode) {
    const CHART_W = 720;
    const CHART_H = 405;

    return (
      <section style={{ width: "100%" }}>
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <h2
            style={{
              marginBottom: 80,
              textAlign: "center",
              fontSize: 20,
              fontWeight: 700,
              color: "#0F1B33",
            }}
          >
            {t("assessment.industryLifeCycle.title")}
          </h2>

          <div
            style={{
              position: "relative",
              width: CHART_W,
              height: CHART_H,
              borderRadius: 16,
              background: "linear-gradient(180deg, #FFFFFF 0%, #EEF2FF 100%)",
              overflow: "visible",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "5%",
                top: "8%",
                bottom: "16%",
                width: 2,
                background: "#1E293B",
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "6px solid transparent",
                  borderRight: "6px solid transparent",
                  borderBottom: "10px solid #1E293B",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  right: 12,
                  transform: "rotate(-90deg) translateY(-50%)",
                  transformOrigin: "right center",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                {t("assessment.industryLifeCycle.industryScore")}
              </span>
            </div>

            <div
              style={{
                position: "absolute",
                left: "5%",
                right: "4%",
                bottom: "16%",
                height: 2,
                background: "#1E293B",
                borderRadius: 2,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  right: -5,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 0,
                  height: 0,
                  borderTop: "6px solid transparent",
                  borderBottom: "6px solid transparent",
                  borderLeft: "10px solid #1E293B",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  right: "50%",
                  top: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#334155",
                }}
              >
                {t("assessment.industryLifeCycle.time")}
              </span>
            </div>

            {[27, 46, 65].map((p) => (
              <div
                key={p}
                style={{
                  position: "absolute",
                  left: "5%",
                  right: "4%",
                  top: `${p}%`,
                  borderTop: "1px dashed #CBD5E1",
                }}
              />
            ))}

            <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>
              <Curve onProgress={() => {}} disableAnimation pdfMode />
            </div>

            <div style={{ position: "absolute", inset: 0, zIndex: 2 }}>
              {translatedStages.map((stage, index) => {
                const isCurrent = index === currentStageIndex;
                return (
                  <Stage
                    key={stage.id}
                    stage={stage}
                    isCurrent={isCurrent}
                    isActive
                    index={index}
                    totalStages={translatedStages.length}
                    pdfMode
                  />
                );
              })}
            </div>

            {currentStage && (
              <div
                style={{
                  position: "absolute",
                  left: "40%",
                  top: "50%",
                  zIndex: 30,
                }}
              >
                <div
                  style={{
                    width: 220,
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.97)",
                    padding: 10,
                    boxShadow: `0 15px 40px ${currentStage.color}30, 0 0 60px ${currentStage.color}15`,
                    border: `1.5px solid ${currentStage.color}25`,
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      borderRadius: "12px 12px 0 0",
                      background: `linear-gradient(90deg, ${currentStage.color}, ${currentStage.color}60)`,
                    }}
                  />

                  <div style={{ marginTop: 8, textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: 9,
                        fontWeight: 600,
                        color: "#64748B",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {t("company.fields.name")}
                    </div>
                    <div
                      style={{
                        marginTop: 2,
                        fontSize: 12,
                        fontWeight: 800,
                        color: "#0F1B33",
                      }}
                    >
                      {companyData.name}
                    </div>
                  </div>

                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 12,
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 8, color: "#64748B" }}>
                        {t("assessment.industryLifeCycle.score")}
                      </div>
                      <div
                        style={{
                          marginTop: 2,
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 800,
                          background: `linear-gradient(135deg, ${currentStage.color}, ${currentStage.color}cc)`,
                          margin: "0 auto",
                        }}
                      >
                        {companyData.score}
                      </div>
                    </div>

                    <div
                      style={{
                        width: 1,
                        height: 36,
                        background: "#E2E8F0",
                      }}
                    />

                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 8, color: "#64748B" }}>
                        {t("assessment.industryLifeCycle.currentPosition")}
                      </div>
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 12,
                          fontWeight: 800,
                          color: currentStage.color,
                        }}
                      >
                        {currentStage.title}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // NORMAL MODE
  return (
    <section className="w-full">
      <div className="mx-auto w-full">
        <motion.h2
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="mb-4 px-2 text-center text-base font-bold text-brand-900 dark:text-white sm:mb-6 sm:text-xl md:mb-16 md:text-2xl"
        >
          {t("assessment.industryLifeCycle.title")}
        </motion.h2>

        <div className="w-full pb-4">
          <div
            className="mx-auto"
            style={{
              width: isMobile ? CHART_WIDTH : "100%",
              minWidth: isMobile ? CHART_WIDTH : "auto",
              padding: isMobile ? "0 16px" : 0,
            }}
          >
            <div
              className="relative rounded-2xl bg-brand-50 dark:bg-navy-900"
              style={{
                width: "100%",
                aspectRatio: "16 / 9",
                minHeight: "250px",
                maxHeight: "450px",
                overflow: "visible",
              }}
            >
              <div className="dark:to-navy-950 absolute inset-0 rounded-2xl bg-gradient-to-b from-white via-brand-50 to-brand-100 dark:from-navy-800 dark:via-navy-900" />

              {/* ============ Left Axis ============ */}
              <div className="absolute bottom-[16%] left-[5%] top-[8%]">
                <div className="relative h-full w-[2px] rounded-full bg-brand-800 dark:bg-brand-300">
                  <div className="border-l-transparent border-r-transparent absolute -top-3 left-1/2 h-0 w-0 -translate-x-1/2 border-b-[10px] border-l-[6px] border-r-[6px] border-b-brand-800 dark:border-b-brand-300" />
                </div>
                <span
                  className={`absolute -right-14 top-1/3 -rotate-90 whitespace-nowrap text-[8px] font-semibold text-brand-700 dark:text-brand-300 sm:text-[10px] md:text-[14px]`}
                >
                  {t("assessment.industryLifeCycle.industryScore")}
                </span>
              </div>

              {/* ============ Bottom Axis ============ */}
              <div className="absolute bottom-[16%] left-[5%] right-[4%]">
                <div className="relative h-[2px] rounded-full bg-brand-800 dark:bg-brand-300">
                  <div className="border-y-transparent absolute right-[-5px] top-1/2 h-0 w-0 -translate-y-1/2 border-y-[6px] border-l-[10px] border-l-brand-800 dark:border-l-brand-300" />
                </div>
                <span className="absolute right-1/2 top-3 text-[8px] font-semibold text-brand-700 dark:text-brand-300 sm:text-[10px] md:text-[14px]">
                  {t("assessment.industryLifeCycle.time")}
                </span>
              </div>

              {/* ============ Guide Lines ============ */}
              <div className="absolute left-[5%] right-[4%] top-[27%] border-t border-dashed border-brand-200 dark:border-brand-700" />
              <div className="absolute left-[5%] right-[4%] top-[46%] border-t border-dashed border-brand-200 dark:border-brand-700" />
              <div className="absolute left-[5%] right-[4%] top-[65%] border-t border-dashed border-brand-200 dark:border-brand-700" />

              {/* ============ Curve ============ */}
              <div className="absolute inset-0 z-0">
                <Curve
                  onProgress={handleStageActivation}
                  disableAnimation={disableAnimation}
                />
              </div>

              {/* ============ Stages ============ */}
              <div className="absolute inset-0 z-10">
                {translatedStages.map((stage, index) => {
                  const isActive = index <= activeStageIndex;
                  const isCurrent = index === currentStageIndex;

                  if (isMobile && !isActive && !isCurrent) {
                    return null;
                  }

                  return (
                    <Stage
                      key={stage.id}
                      stage={stage}
                      isCurrent={isCurrent}
                      isActive={isActive}
                      index={index}
                      totalStages={translatedStages.length}
                    />
                  );
                })}
              </div>

              {/* ============ Company Info Card ============ */}
              {animationComplete && currentStage && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.3, ease: "easeOut" }}
                  className={`absolute left-[40%] top-[50%] z-30`}
                >
                  <div
                    className={`${
                      isMobile ? "w-48" : "w-56 sm:w-60"
                    } rounded-xl bg-white/95 p-2 shadow-xl backdrop-blur-sm dark:bg-navy-800/95`}
                    style={{
                      boxShadow: `0 15px 40px ${currentStage.color}30, 0 0 60px ${currentStage.color}15`,
                      border: `1.5px solid ${currentStage.color}25`,
                    }}
                  >
                    <div
                      className="absolute left-0 right-0 top-0 h-1 rounded-t-xl"
                      style={{
                        background: `linear-gradient(90deg, ${currentStage.color}, ${currentStage.color}60)`,
                      }}
                    />

                    <div className="relative z-10">
                      <div className="my-1.5 text-center sm:my-2">
                        <div className="text-[10px] font-medium uppercase tracking-wider text-gray-800 dark:text-gray-500 sm:text-[12px]">
                          {t("company.fields.name")}
                        </div>
                        <div className="mt-0.5 truncate px-1 text-xs font-bold text-gray-800 dark:text-white sm:mt-1 sm:text-sm">
                          {companyData.name}
                        </div>
                      </div>

                      <div
                        className={`flex items-start justify-center gap-3 sm:gap-4 ${
                          isMobile ? "gap-2" : ""
                        }`}
                      >
                        <div className="text-center">
                          <div className="text-[8px] font-medium text-gray-800 dark:text-gray-500 sm:text-[10px]">
                            {t("assessment.industryLifeCycle.score")}
                          </div>
                          <div
                            className={`mx-auto mt-0.5 flex items-center justify-center rounded-full text-xs font-bold text-white sm:text-sm ${
                              isMobile ? "h-8 w-8" : "h-9 w-9 sm:h-10 sm:w-10"
                            }`}
                            style={{
                              background: `linear-gradient(135deg, ${currentStage.color}, ${currentStage.color}cc)`,
                              boxShadow: `0 3px 15px ${currentStage.color}40`,
                            }}
                          >
                            {companyData.score}
                          </div>
                        </div>

                        <div
                          className={`${
                            isMobile ? "h-8" : "h-9 sm:h-10"
                          } w-px bg-gray-200 dark:bg-gray-600`}
                        />

                        <div className="text-center">
                          <div className="text-[8px] font-medium text-gray-800 dark:text-gray-500 sm:text-[10px]">
                            {t("assessment.industryLifeCycle.currentPosition")}
                          </div>
                          <div
                            className="mt-1 text-xs font-extrabold sm:mt-2 sm:text-sm"
                            style={{ color: currentStage.color }}
                          >
                            {currentStage.title}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {showBottomCards && <BottomCards />}
      </div>
    </section>
  );
}
